import React from 'react';
import cn from 'classnames';

import {useLocalesMgr} from 'coreHooks';
import {getIsInternalUrl} from 'coreUtils/url';

import Link from 'brandComponents/Link';
import {useURLs} from 'brandHooks';

import ButtonChildren from './Subcomponents/ButtonChildren';
import {IButtonProps} from './IButton';

import styles from './Button.module.scss';

const Button = ({
	children,
	classNames,
	contentClassNames,
	disabled,
	onClick,
	type = 'button',
	variant = 'primary',
	loading,
	showAsLink,
	onMouseDown,
	htmlId,
	ariaLabel,
	title,
	url,
	target,
	tabIndex,
	fullWidth,
	dataIds,
	ref,
}: IButtonProps) => {
	const urls = useURLs();
	const localesMgr = useLocalesMgr();

	const isInternalUrl = getIsInternalUrl(url);
	let realUrl = null;

	if (url && isInternalUrl) {
		realUrl = localesMgr.updateLinkWithLocale(url);
	} else if (url) {
		realUrl = url;
	}

	const buttonClassNames = cn(
		styles['btn'],
		classNames,
		{
			[styles[`btn--${variant}`]]: variant,
			[styles['btn--loading']]: loading,
			[styles['btn--link']]: showAsLink,
			[styles['btn--full-width']]: fullWidth
		}
	);

	const buttonAttrs: React.AnchorHTMLAttributes<{}> = {
		title,
		className: buttonClassNames,
		'aria-label': ariaLabel,
	};

	if (target) {
		buttonAttrs.target = target;
	}

	if (!isInternalUrl && !url.startsWith(urls.CART_PROXY)) {
		buttonAttrs.rel = 'noopener noreferrer';
	}

	if (onClick) {
		buttonAttrs.onClick = onClick;
	}

	if (realUrl) {
		// We cannot use the React Router's Link component for external links
		// TODO: Undestand if title is correct and if it is ever used.
		if (!isInternalUrl) {
			return (
				<a {...buttonAttrs} href={realUrl} {...dataIds}>
					{title ||
						<ButtonChildren contentClassNames={contentClassNames} loading={loading}>{children}</ButtonChildren>
					}
				</a>
			);
		}

		return (
			<Link {...buttonAttrs} to={realUrl} ref={ref} {...dataIds}>
				<ButtonChildren contentClassNames={contentClassNames} loading={loading}>{children}</ButtonChildren>
			</Link>
		);
	}

	return (
		<button
			className={buttonClassNames}
			disabled={disabled}
			aria-busy={loading || undefined}
			onClick={onClick}
			onMouseDown={onMouseDown}
			type={type}
			aria-label={ariaLabel}
			title={title}
			ref={ref}
			tabIndex={tabIndex}
			id={htmlId}
			{...dataIds}
		>
			<ButtonChildren contentClassNames={contentClassNames} loading={loading}>{children}</ButtonChildren>
		</button>
	);
};

export default Button;
